import React, { useState, useEffect } from 'react'
import { Edit, Plus, Search, Settings, Shield, Trash2, User as UserIcon, UserCheck, Users, UserX } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { CreateUserModal } from '../components/users/CreateUserModal'
import { EditUserModal } from '../components/users/EditUserModal'
import { RolePermissionsManager } from '../components/RolePermissionsManager'
import { userService, User, CreateUserData, UpdateUserData } from '../services/userService'
import { parseDbTimestamp } from '../utils/datetime'
import { NativeSelect } from "@/components/ui/native-select"
import { DataTable } from "@/components/ui/data-table"
import { cn } from '@/utils/cn'
import { canonicalRole, isLawyerLike, OFFICE_ROLES, ROLE_LABELS, roleBadgeClass, roleLabel } from '@/lib/roles'

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [filteredUsers, setFilteredUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'users' | 'permissions'>('users')

  // Load users on mount
  useEffect(() => {
    loadUsers()
  }, [])

  // Filter users when search/filter changes
  useEffect(() => {
    let filtered = users

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(user => 
        user.firstName.toLowerCase().includes(term) ||
        user.lastName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term)
      )
    }

    if (roleFilter) {
      filtered = filtered.filter(user => canonicalRole(user.role) === roleFilter)
    }

    if (statusFilter) {
      const isActive = statusFilter === 'active'
      filtered = filtered.filter(user => user.isActive === isActive)
    }

    setFilteredUsers(filtered)
  }, [users, searchTerm, roleFilter, statusFilter])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const users = await userService.getUsers()
      setUsers(users)
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateUser = async (userData: CreateUserData) => {
    try {
      setActionLoading(true)
      // Call the real API
      const newUser = await userService.createUser(userData)
      
      // Reload users to get fresh data from server
      await loadUsers()
      setShowAddModal(false)
      
      // Show success message (you can add toast notifications here)
      console.log('User created successfully:', newUser)
    } catch (error) {
      console.error('Error creating user:', error)
      // Show error message
    } finally {
      setActionLoading(false)
    }
  }

  const handleUpdateUser = async (userData: UpdateUserData) => {
    if (!selectedUser) return

    try {
      setActionLoading(true)
      // Call the real API
      const updatedUser = await userService.updateUser(selectedUser.id, userData)
      
      // Reload users to get fresh data from server
      await loadUsers()
      setShowEditModal(false)
      setSelectedUser(null)
      
      console.log('User updated successfully:', updatedUser)
    } catch (error) {
      console.error('Error updating user:', error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleToggleStatus = async (userId: string) => {
    try {
      // Call the real API
      await userService.toggleUserStatus(userId)
      
      // Reload users to get fresh data from server
      await loadUsers()
      
      console.log('User status toggled:', userId)
    } catch (error) {
      console.error('Error toggling user status:', error)
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) {
      return
    }

    try {
      // Call the real API
      await userService.deleteUser(userId)
      
      // Reload users to get fresh data from server
      await loadUsers()
      
      console.log('User deleted:', userId)
    } catch (error) {
      console.error('Error deleting user:', error)
    }
  }

  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    setShowEditModal(true)
  }

  const columns: ColumnDef<User>[] = [
    {
      id: 'name',
      header: 'Usuário',
      accessorFn: (u) => `${u.firstName} ${u.lastName}`,
      cell: ({ row }) => {
        const user = row.original
        return (
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted">
              <UserIcon className="size-4 text-muted-foreground" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">
                {user.firstName} {user.lastName}
              </div>
              <div className="truncate text-sm text-muted-foreground">{user.email}</div>
            </div>
          </div>
        )
      },
    },
    {
      accessorKey: 'role',
      header: 'Função',
      cell: ({ row }) => (
        <Badge variant="outline" className={roleBadgeClass(row.original.role)}>
          {roleLabel(row.original.role)}
        </Badge>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'default' : 'outline'}>
          {row.original.isActive ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      accessorKey: 'lastLogin',
      header: 'Último Acesso',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.lastLogin
            ? parseDbTimestamp(row.original.lastLogin)?.toLocaleString('pt-BR') ?? 'Nunca acessou'
            : 'Nunca acessou'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original
        const toggleLabel = user.isActive ? 'Desativar' : 'Ativar'
        return (
          <div className="flex items-center justify-end gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleToggleStatus(user.id)}
                  className={user.isActive ? 'text-muted-foreground hover:text-destructive' : 'text-primary'}
                  aria-label={toggleLabel}
                >
                  {user.isActive ? <UserX className="size-4" /> : <UserCheck className="size-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{toggleLabel}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={() => handleEditUser(user)} aria-label="Editar">
                  <Edit className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Editar</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteUser(user.id)}
                  className="text-muted-foreground hover:text-destructive"
                  aria-label="Excluir"
                >
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Excluir</TooltipContent>
            </Tooltip>
          </div>
        )
      },
    },
  ]

  const hasFilters = Boolean(searchTerm || roleFilter || statusFilter)
  const stats = [
    { label: 'Total', value: users.length, icon: Users, className: undefined },
    { label: 'Ativos', value: users.filter(u => u.isActive).length, icon: UserCheck, className: 'text-primary' },
    { label: 'Inativos', value: users.filter(u => !u.isActive).length, icon: UserX, className: undefined },
    { label: 'Advogados', value: users.filter(u => isLawyerLike(u.role)).length, icon: Shield, className: undefined },
  ]

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Users className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Usuários</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie usuários e permissões do sistema</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Plus className="size-4" /> Novo Usuário
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(next) => setActiveTab(next as 'users' | 'permissions')}>
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="users"><Users className="size-4" /> Usuários</TabsTrigger>
          <TabsTrigger value="permissions"><Settings className="size-4" /> Perfis e Permissões</TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'permissions' ? (
        <RolePermissionsManager />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {stats.map(({ label, value, icon: Icon, className }) => (
              <Card key={label} size="sm">
                <CardContent className="flex items-center gap-3">
                  <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="truncate text-xs text-muted-foreground">{label}</p>
                    <p className={cn('text-lg font-semibold tabular-nums', className)}>{value}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_12rem_12rem_auto] lg:items-center">
            <div className="relative min-w-0 sm:col-span-2 lg:col-span-1">
              <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                aria-label="Buscar usuários"
                placeholder="Buscar usuários..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            <NativeSelect aria-label="Filtrar por função" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="">Todas as funções</option>
              {OFFICE_ROLES.map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </NativeSelect>
            <NativeSelect aria-label="Filtrar por status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </NativeSelect>
            {hasFilters && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchTerm('')
                  setRoleFilter('')
                  setStatusFilter('')
                }}
              >
                Limpar filtros
              </Button>
            )}
          </div>

          <Card className="min-w-0 gap-0 py-0">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-medium">Usuários ({filteredUsers.length})</h2>
            </div>
            {loading ? (
              <div className="flex flex-col gap-2 p-4" aria-busy="true">
                <span className="sr-only">Carregando usuários...</span>
                {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}
              </div>
            ) : (
              <div className="min-w-0 p-2">
                <DataTable
                  columns={columns}
                  data={filteredUsers}
                  minWidth="min-w-[720px]"
                  emptyMessage="Nenhum usuário encontrado."
                  pageSize={20}
                />
              </div>
            )}
          </Card>

          <CreateUserModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSubmit={handleCreateUser}
            loading={actionLoading}
          />

          <EditUserModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false)
              setSelectedUser(null)
            }}
            onSubmit={handleUpdateUser}
            user={selectedUser}
            loading={actionLoading}
          />
        </>
      )}
    </div>
  )
}
